<template>
  <div class="flex justify-center" aria-hidden="true">
    <div class="h-2 overflow-hidden" :style="{ width: `${windowWidth}px` }">
      <div
        class="flex transition-transform duration-300 ease-out"
        :style="{ transform: `translateX(${offset}px)` }"
      >
        <span
          v-for="dot in dots"
          :key="dot.page"
          class="flex h-2 w-5 shrink-0 items-center justify-center"
        >
          <span
            class="rounded-full transition-all duration-300 ease-out"
            :class="[dot.size, dot.color, dot.opacity]"
          />
        </span>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import { useOnboardingStore } from '@/features/onboarding/store'

const VISIBLE_DOTS = 7
const SLOT_WIDTH = 20

const onboardingStore = useOnboardingStore()

const total = computed(() => onboardingStore.maxSteps)
const visible = computed(() => Math.min(VISIBLE_DOTS, total.value))
const current = computed(() => onboardingStore.step - 1)

const windowStart = computed(() =>
  Math.max(
    0,
    Math.min(
      current.value - Math.floor(visible.value / 2),
      total.value - visible.value
    )
  )
)

const windowWidth = computed(() => visible.value * SLOT_WIDTH)
const offset = computed(() => -windowStart.value * SLOT_WIDTH)

const dots = computed(() => {
  const start = windowStart.value
  const hasPrevious = start > 0
  const hasNext = start + visible.value < total.value

  return Array.from({ length: total.value }, (_, page) => {
    const slot = page - start

    if (slot < 0 || slot >= visible.value) {
      return {
        page,
        size: 'size-1',
        color: 'bg-gray-300/50',
        opacity: 'opacity-0',
      }
    }

    if (page === current.value) {
      return {
        page,
        size: 'size-2',
        color: 'bg-green-900',
        opacity: 'opacity-100',
      }
    }

    const fromEdge = Math.min(
      hasPrevious ? slot : visible.value,
      hasNext ? visible.value - 1 - slot : visible.value
    )

    return {
      page,
      size: fromEdge === 0 ? 'size-1' : fromEdge === 1 ? 'size-1.5' : 'size-2',
      color: 'bg-gray-300/50',
      opacity: 'opacity-100',
    }
  })
})
</script>
